import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Accessibility,
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  GET_DISABILITY_DEGREES,
  CREATE_DISABILITY_DEGREE_MUTATION,
  UPDATE_DISABILITY_DEGREE_MUTATION,
  DELETE_DISABILITY_DEGREE_MUTATION,
} from '../../graphql/queries';

interface DisabilityDegree {
  id: string;
  name: string;
  maxWorkHoursPerDay: number;
  breakDurationMinutes: number;
  additionalBreakMinutes: number;
  description?: string;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  employeeCount?: number;
}

interface FormData {
  name: string;
  maxWorkHoursPerDay: number;
  breakDurationMinutes: number;
  additionalBreakMinutes: number;
  description: string;
  isActive: boolean;
  sortOrder: number;
}

const EMPTY_FORM: FormData = {
  name: '',
  maxWorkHoursPerDay: 8,
  breakDurationMinutes: 15,
  additionalBreakMinutes: 0,
  description: '',
  isActive: true,
  sortOrder: 0,
};

export const DisabilityDegreeManagement: React.FC = () => {
  const { data, loading, error, refetch } = useQuery(GET_DISABILITY_DEGREES, {
    fetchPolicy: 'network-only',
  });

  const [createDegree] = useMutation(CREATE_DISABILITY_DEGREE_MUTATION);
  const [updateDegree] = useMutation(UPDATE_DISABILITY_DEGREE_MUTATION);
  const [deleteDegree] = useMutation(DELETE_DISABILITY_DEGREE_MUTATION);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DisabilityDegree | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<DisabilityDegree | null>(null);
  const [deletingInProgress, setDeletingInProgress] = useState(false);

  const openCreateDialog = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (degree: DisabilityDegree) => {
    setEditing(degree);
    setFormData({
      name: degree.name,
      maxWorkHoursPerDay: Number(degree.maxWorkHoursPerDay),
      breakDurationMinutes: degree.breakDurationMinutes,
      additionalBreakMinutes: degree.additionalBreakMinutes,
      description: degree.description || '',
      isActive: degree.isActive,
      sortOrder: degree.sortOrder,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (degree: DisabilityDegree) => {
    setDeleting(degree);
    setDeleteDialogOpen(true);
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Nazwa stopnia jest wymagana';
    if (formData.name.length > 100) return 'Nazwa może mieć maksymalnie 100 znaków';
    if (formData.maxWorkHoursPerDay < 0 || formData.maxWorkHoursPerDay > 24)
      return 'Liczba godzin pracy musi być między 0 a 24';
    if (formData.breakDurationMinutes < 0 || formData.breakDurationMinutes > 480)
      return 'Czas przerwy musi być między 0 a 480 minut';
    if (formData.additionalBreakMinutes < 0 || formData.additionalBreakMinutes > 480)
      return 'Dodatkowa przerwa musi być między 0 a 480 minut';
    if (formData.description.length > 500) return 'Opis może mieć maksymalnie 500 znaków';
    return null;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) {
      toast.error(err);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        maxWorkHoursPerDay: formData.maxWorkHoursPerDay,
        breakDurationMinutes: formData.breakDurationMinutes,
        additionalBreakMinutes: formData.additionalBreakMinutes,
        description: formData.description.trim() || null,
        isActive: formData.isActive,
        sortOrder: formData.sortOrder,
      };

      if (editing) {
        await updateDegree({ variables: { id: editing.id, data: payload } });
        toast.success(`Stopień "${formData.name}" został zaktualizowany`);
      } else {
        await createDegree({ variables: { data: payload } });
        toast.success(`Stopień "${formData.name}" został utworzony`);
      }
      setDialogOpen(false);
      refetch();
    } catch (e: any) {
      toast.error(e.message || 'Wystąpił błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeletingInProgress(true);
    try {
      await deleteDegree({ variables: { id: deleting.id } });
      toast.success(`Stopień "${deleting.name}" został usunięty`);
      setDeleteDialogOpen(false);
      setDeleting(null);
      refetch();
    } catch (e: any) {
      toast.error(e.message || 'Wystąpił błąd podczas usuwania');
    } finally {
      setDeletingInProgress(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-muted-foreground">Ładowanie stopni niepełnosprawności...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-500">Błąd podczas ładowania: {error.message}</div>
      </div>
    );
  }

  const degrees: DisabilityDegree[] = (data?.disabilityDegrees || [])
    .slice()
    .sort((a: DisabilityDegree, b: DisabilityDegree) => a.sortOrder - b.sortOrder);

  const activeCount = degrees.filter((d) => d.isActive).length;
  const totalAssigned = degrees.reduce((sum, d) => sum + (d.employeeCount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Stopnie niepełnosprawności</h2>
          <p className="text-muted-foreground">
            Konfiguracja stopni niepełnosprawności pracowników z parametrami czasu pracy i przerw
          </p>
        </div>
        <Button onClick={openCreateDialog} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Dodaj stopień</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-950/40 rounded-lg">
                <Accessibility className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Wszystkie stopnie</p>
                <p className="text-2xl font-bold text-foreground">{degrees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-green-100 dark:bg-green-950/40 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Aktywne</p>
                <p className="text-2xl font-bold text-foreground">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 dark:bg-purple-950/40 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Przypisanych pracowników</p>
                <p className="text-2xl font-bold text-foreground">{totalAssigned}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista stopni</CardTitle>
          <CardDescription>
            Stopnie zgodne z polską ustawą z 27.08.1997 o rehabilitacji zawodowej.
            Wartości domyślne: Lekki (8h, 15 min przerwy), Umiarkowany/Znaczny (7h, 15+15 min przerwy).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nazwa</TableHead>
                <TableHead className="text-center">Max godz./dzień</TableHead>
                <TableHead className="text-center">Przerwa (min)</TableHead>
                <TableHead className="text-center">Dod. przerwa (min)</TableHead>
                <TableHead>Opis</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Pracownicy</TableHead>
                <TableHead>Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {degrees.map((degree) => (
                <TableRow key={degree.id}>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <div className="font-medium text-foreground">{degree.name}</div>
                      {degree.isDefault && (
                        <Badge variant="outline" className="text-xs">
                          Domyślny
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center font-mono">
                    {Number(degree.maxWorkHoursPerDay).toFixed(2).replace(/\.00$/, '')}
                  </TableCell>
                  <TableCell className="text-center font-mono">
                    {degree.breakDurationMinutes}
                  </TableCell>
                  <TableCell className="text-center font-mono">
                    {degree.additionalBreakMinutes}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {degree.description || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant={degree.isActive ? 'default' : 'secondary'}
                      className={
                        degree.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400'
                          : ''
                      }
                    >
                      {degree.isActive ? (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Aktywny
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3 mr-1" />
                          Nieaktywny
                        </>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline">{degree.employeeCount || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(degree)}
                        aria-label={`Edytuj ${degree.name}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {!degree.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                          onClick={() => openDeleteDialog(degree)}
                          aria-label={`Usuń ${degree.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edytuj stopień niepełnosprawności' : 'Dodaj nowy stopień'}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? 'Zmień parametry wybranego stopnia niepełnosprawności.'
                : 'Wypełnij dane nowego stopnia niepełnosprawności.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="name">
                Nazwa <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="np. Lekki"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={100}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="maxWorkHoursPerDay">
                  Max godzin / dzień <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="maxWorkHoursPerDay"
                  type="number"
                  step="0.25"
                  min={0}
                  max={24}
                  value={formData.maxWorkHoursPerDay}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxWorkHoursPerDay: parseFloat(e.target.value) || 0,
                    })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sortOrder">Kolejność</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  min={0}
                  value={formData.sortOrder}
                  onChange={(e) =>
                    setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="breakDurationMinutes">
                  Czas przerwy (min) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="breakDurationMinutes"
                  type="number"
                  min={0}
                  max={480}
                  value={formData.breakDurationMinutes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      breakDurationMinutes: parseInt(e.target.value) || 0,
                    })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="additionalBreakMinutes">
                  Dodatkowa przerwa (min)
                </Label>
                <Input
                  id="additionalBreakMinutes"
                  type="number"
                  min={0}
                  max={480}
                  value={formData.additionalBreakMinutes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      additionalBreakMinutes: parseInt(e.target.value) || 0,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Np. 15 min dla stopnia umiarkowanego/znacznego (art. 17)
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Opis</Label>
              <Input
                id="description"
                placeholder="Krótki opis stopnia..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={500}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                id="isActive"
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-input"
                disabled={editing?.isDefault}
              />
              <Label htmlFor="isActive" className="cursor-pointer">
                Aktywny
              </Label>
              {editing?.isDefault && (
                <span className="text-xs text-muted-foreground">
                  (domyślny stopień nie może być dezaktywowany)
                </span>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Anuluj
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Zapisywanie...' : editing ? 'Zapisz zmiany' : 'Utwórz stopień'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Potwierdź usunięcie</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć stopień "{deleting?.name}"? Ta operacja jest
              nieodwracalna.
            </DialogDescription>
          </DialogHeader>
          {deleting && (deleting.employeeCount || 0) > 0 && (
            <div className="p-3 text-sm text-amber-700 bg-amber-50 dark:bg-amber-950/20 dark:text-amber-400 rounded-md">
              Do tego stopnia jest przypisanych {deleting.employeeCount} pracowników. Aby
              usunąć stopień, najpierw przepisz tych pracowników na inny stopień.
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleting(null);
              }}
              disabled={deletingInProgress}
            >
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deletingInProgress}
            >
              {deletingInProgress ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
